import React, { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import api, { formatApiError } from "../api";
import { BrutalButton } from "./Brutal";
import { PaperPlaneTilt, EnvelopeSimple, X } from "@phosphor-icons/react";
import { toast } from "sonner";

/** v414.44 — compose a mass update email to every active distributor with an
 * email on file. Prefilled with the latest feature highlights; supports a
 * test-send to yourself before the real blast. */
export default function AnnouncementDialog({ onClose }) {
  const { t } = useTranslation();
  const [subject, setSubject] = useState(t("announce.default_subject"));
  const [body, setBody] = useState(t("announce.default_body"));
  const [recipients, setRecipients] = useState(null);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    api.get("/announcements/recipients")
      .then((r) => setRecipients(r.data))
      .catch((e) => toast.error(formatApiError(e)));
  }, []);

  const send = async (testSend) => {
    if (!testSend && !window.confirm(t("announce.confirm", { count: recipients?.count ?? 0 }))) return;
    setBusy(true);
    try {
      const { data } = await api.post("/announcements/send", {
        subject: subject.trim(),
        body,
        test_send: testSend,
      });
      if (testSend) {
        toast.success(data.sent ? t("announce.test_sent") : t("announce.test_failed"));
      } else {
        toast.success(t("announce.sent", { sent: data.sent, failed: data.failed }));
        onClose();
      }
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 bg-black/50 flex items-center justify-center p-4 overflow-y-auto" data-testid="announce-dialog">
      <div className="bg-white rounded-2xl border border-zinc-200 shadow-xl w-full max-w-xl p-5 my-8">
        <div className="flex items-start justify-between gap-3 mb-1">
          <h2 className="text-lg font-bold flex items-center gap-2">
            <EnvelopeSimple size={20} weight="bold" className="text-[#8a6a1c]" />
            {t("announce.title")}
          </h2>
          <button onClick={onClose} className="text-zinc-400 hover:text-zinc-700" data-testid="announce-close-x">
            <X size={20} weight="bold" />
          </button>
        </div>
        <p className="text-xs text-zinc-500 mb-4">
          {recipients === null
            ? t("common.loading")
            : t("announce.recipient_count", { count: recipients.count })}
        </p>

        <label className="block text-xs font-semibold mb-1 text-zinc-700">{t("announce.subject_label")}</label>
        <input
          type="text"
          value={subject}
          maxLength={160}
          onChange={(e) => setSubject(e.target.value)}
          className="w-full h-10 px-3 text-sm border border-zinc-300 rounded-lg mb-3 focus:outline-none focus:ring-2 focus:ring-[#c9a24a]/50"
          data-testid="announce-subject-input"
        />

        <label className="block text-xs font-semibold mb-1 text-zinc-700">{t("announce.body_label")}</label>
        <textarea
          value={body}
          onChange={(e) => setBody(e.target.value)}
          rows={14}
          className="w-full px-3 py-2 text-sm border border-zinc-300 rounded-lg font-mono focus:outline-none focus:ring-2 focus:ring-[#c9a24a]/50"
          data-testid="announce-body-input"
        />
        <p className="text-[11px] text-zinc-400 mt-1">{t("announce.format_hint")}</p>

        {recipients?.recipients?.length > 0 && (
          <details className="mt-3">
            <summary className="text-xs font-semibold text-zinc-600 cursor-pointer" data-testid="announce-recipients-toggle">
              {t("announce.view_recipients", { count: recipients.count })}
            </summary>
            <div className="mt-2 max-h-36 overflow-y-auto border border-zinc-200 rounded-lg p-2 text-xs text-zinc-600 space-y-1">
              {recipients.recipients.map((r) => (
                <div key={r.id} className="flex justify-between gap-2">
                  <span className="font-medium">{r.name}</span>
                  <span className="text-zinc-400 truncate">{r.email}</span>
                </div>
              ))}
            </div>
          </details>
        )}

        <div className="flex flex-wrap gap-2 mt-4">
          <BrutalButton
            variant="secondary"
            disabled={busy || !subject.trim() || body.trim().length < 10}
            onClick={() => send(true)}
            data-testid="announce-test-btn"
          >
            {t("announce.test_btn")}
          </BrutalButton>
          <BrutalButton
            disabled={busy || !recipients?.count || !subject.trim() || body.trim().length < 10}
            onClick={() => send(false)}
            data-testid="announce-send-btn"
          >
            <PaperPlaneTilt size={16} weight="bold" />
            {busy ? t("common.loading") : t("announce.send_btn", { count: recipients?.count ?? 0 })}
          </BrutalButton>
        </div>
      </div>
    </div>
  );
}
